/**
 * Revenue Chart Component
 * Shows revenue trends over time with scenario comparison
 */

import React from 'react';
import {
  ChartContainer,
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  CustomTooltip,
  formatCurrency,
  SCENARIO_COLORS
} from './FinancialChart';
import { FinancialProjection, ScenarioType } from '../../../types/financialForecasting';

interface RevenueChartProps {
  data: FinancialProjection[];
  activeScenario?: ScenarioType;
  showAllScenarios?: boolean;
  chartType?: 'line' | 'area';
}

export const RevenueChart: React.FC<RevenueChartProps> = ({
  data,
  activeScenario = 'realistic',
  showAllScenarios = false,
  chartType = 'area'
}) => {
  // Prepare chart data
  const chartData = data.map((projection) => ({
    period: projection.period,
    revenue: projection.totalRevenue
  }));

  const Chart = chartType === 'area' ? AreaChart : LineChart;

  return (
    <ChartContainer
      title="Revenue Projection"
      subtitle="Total revenue over time"
    >
      <ResponsiveContainer width="100%" height="100%">
        <Chart data={chartData}>
          <defs>
            <linearGradient id="realisticGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={SCENARIO_COLORS.realistic} stopOpacity={0.3} />
              <stop offset="95%" stopColor={SCENARIO_COLORS.realistic} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="optimisticGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={SCENARIO_COLORS.optimistic} stopOpacity={0.3} />
              <stop offset="95%" stopColor={SCENARIO_COLORS.optimistic} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="pessimisticGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={SCENARIO_COLORS.pessimistic} stopOpacity={0.3} />
              <stop offset="95%" stopColor={SCENARIO_COLORS.pessimistic} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis
            dataKey="period"
            stroke="#6B7280"
            fontSize={12}
            tickLine={false}
          />
          <YAxis
            stroke="#6B7280"
            fontSize={12}
            tickFormatter={formatCurrency}
            tickLine={false}
          />
          <Tooltip
            content={<CustomTooltip formatter={formatCurrency} />}
          />
          <Legend
            wrapperStyle={{ paddingTop: '20px' }}
            iconType="circle"
          />
          
          {chartType === 'area' ? (
            <Area
              type="monotone"
              dataKey="revenue"
              name={"Revenue"}
              stroke={SCENARIO_COLORS[activeScenario]}
              fill={`url(#${activeScenario}Gradient)`}
              strokeWidth={3}
              dot={false}
            />
          ) : (
            <Line
              type="monotone"
              dataKey="revenue"
              name={"Revenue"}
              stroke={SCENARIO_COLORS[activeScenario]}
              strokeWidth={3}
              dot={false}
            />
          )}
        </Chart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

export default RevenueChart;
